/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useRef, useState } from 'react';
import { DayPicker, SelectSingleEventHandler } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns/format';
import {
  bathingDates,
  getDateString,
  offeringEndsAt,
  offeringStartsFrom,
  validateInRange,
} from '@camp67/model/booking/dates';
import { differenceInDays, subDays } from 'date-fns';
import { snackbarState } from '../snackbar/snackbar-state';
import clsx from 'clsx';
import Sprite from '../../sprites/sprite';
import { TENT_TYPE_V1_TO_NIVAAS_SLUG, TentTypeV1 } from '@camp67/model';
import { RoomsPerDay } from '../../network/query-tent-availability';

interface DateFieldSingleProps {
  label: string;
  isRequired?: boolean;
  isMobile?: boolean;
  error?: string | null;
  onChange: (dateFrom?: Date, dateTo?: Date) => void;
  min: Date;
  max: Date;
  value: Date | undefined;
  // mappping of dates to available room ids.
  // each id has shape "nivaas:camp67:room/type/dorm-single/room/00001"
  availableDates: RoomsPerDay;
  tents: [TentTypeV1, number][];
  clearEndDate?: (f: Date) => void;
}

const doesRangeIncludeBathingDates = (from: Date, to: Date) => {
  return bathingDates.some((date) => date >= from && date <= to);
};

const isRangeAtLeastThreeDays = (from: Date, to: Date) => {
  return differenceInDays(to, from) + 1 >= 3;
};

export const isValidTimeRange = (from: Date, to: Date) => {
  return !doesRangeIncludeBathingDates(from, to) || isRangeAtLeastThreeDays(from, to);
};

const SNACKBAR_ERROR_ID = 'booking-date-field';

function validateOnlyStartDatePresent(from: Date) {
  return validateInRange(from);
}

export const isDisabled = (
  date: Date,
  rooms: [TentTypeV1, number][],
  roomsPerDay: RoomsPerDay,
  fromDate?: Date,
): boolean => {
  // If the date is before today or after the offering ends, it should be disabled.
  if (date < subDays(new Date(), 1) || date >= offeringEndsAt) {
    return true;
  }

  return isUnavailable(date, rooms, roomsPerDay, fromDate);
};

export const isUnavailable = (
  date: Date,
  rooms: [TentTypeV1, number][],
  roomsPerDay: RoomsPerDay,
  fromDate?: Date,
): boolean => {
  // Ignore days that are disabled, e.g. days before today or after the offering ends.
  if (date < subDays(new Date(), 1) || date >= offeringEndsAt) {
    return false;
  }

  // There are two cases. Given date with no fromDate (check in date), or given date with fromDate (check out date).

  // Case 1: Given date with no fromDate (check in date).
  // Simply check that the date is available in the keys of roomsPerDay, and if so, check that
  // the number of available rooms on that offering is >= the quantity requested for each tenttypev1 room in `rooms`.
  // Note that for a room id in the value of roomsPerDay[date], the room id has shape "nivaas:camp67:room/type/dorm-single/room/00001"
  // Using TENT_TYPE_V1_TO_NIVAAS_SLUG you can go from TentTypeV1 to the nivaas slug, i.e. to get "dorm-single" from TentTypeV1.DORM_SINGLE.

  if (!fromDate) {
    const dateString = getDateString(date);
    const availableRooms = roomsPerDay[dateString];
    if (!availableRooms) {
      return true;
    }

    for (const [room, quantity] of rooms) {
      const nivaasSlug = TENT_TYPE_V1_TO_NIVAAS_SLUG[room];

      const roomIds = availableRooms.filter((id) => id?.includes(nivaasSlug));
      if (roomIds.length < quantity) {
        return true;
      }
    }

    return false;
  }

  // Ignore days that are disabled, e.g. days before from date
  if (date < fromDate) {
    return false;
  }

  // Ignore same date
  if (date.getTime() === fromDate.getTime()) {
    return false;
  }

  // Case 2: Given date with fromDate (check out date).
  // In this case, what we need to ensure is that for all the dates between the fromDate and date - 1, the
  // unique room ids that are available *for each offering, across each day* (i.e. an intersection of the room ids) is
  // >= the quantity requested for each tenttypev1 room in `rooms`.

  // Note: we don't need to check the checkout date as you can checkout on a date no matter what the availability on that
  // day is, but the day before should be available.
  const roomTypeToIntersection: Record<string, Set<string>> = {};
  for (let d = new Date(fromDate); d < date; d.setDate(d.getDate() + 1)) {
    const dateString = getDateString(d);
    const availableRooms = roomsPerDay[dateString];
    if (!availableRooms) {
      return true;
    }

    for (const [room, quantity] of rooms) {
      const nivaasSlug = TENT_TYPE_V1_TO_NIVAAS_SLUG[room];
      const roomIds = availableRooms.filter((id) => id?.includes(nivaasSlug));
      if (roomIds.length < quantity) {
        return true;
      }

      if (!roomTypeToIntersection[nivaasSlug]) {
        roomTypeToIntersection[nivaasSlug] = new Set(roomIds);
      } else {
        const existing = roomTypeToIntersection[nivaasSlug];
        const intersection = new Set([...existing].filter((id) => roomIds?.includes(id)));
        roomTypeToIntersection[nivaasSlug] = intersection;
      }
    }
  }

  for (const [room, quantity] of rooms) {
    const nivaasSlug = TENT_TYPE_V1_TO_NIVAAS_SLUG[room];
    if (!roomTypeToIntersection[nivaasSlug]) {
      return true;
    }

    if (roomTypeToIntersection[nivaasSlug].size < quantity) {
      return true;
    }
  }

  return false;
};

export const DateFieldSingle: React.FC<DateFieldSingleProps> = ({
  isRequired = false,
  isMobile = false,
  error,
  label,
  onChange,
  min,
  max,
  value,
  availableDates,
  tents,
  clearEndDate,
}) => {
  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  const hasError = snackbarState.message && snackbarState.id === SNACKBAR_ERROR_ID;

  const onEscape = useCallback((evt: React.KeyboardEvent) => {
    if (evt.code === 'Escape') {
      setOpen(false);
    }
  }, []);

  const onDateChange: SelectSingleEventHandler = useCallback(
    (date) => {
      if (!date) {
        return;
      }

      if (!validateOnlyStartDatePresent(date)) {
        return;
      }

      onChange(date);
      clearEndDate?.(date);

      onClose();

      const endDateField = document.getElementById(
        `end-date-field${isMobile ? '-mobile' : ''}`,
      );
      endDateField?.focus();
      endDateField?.click();
    },

    [clearEndDate, isMobile, onChange, onClose],
  );

  const dates = Object.keys(availableDates).map((date) => getDateString(new Date(date)));

  return (
    <div
      className="w-full flex flex-col gap-2 mt-5 relative z-50"
      data-container={label + `${isMobile ? '-mobile' : ''}`}
    >
      <div
        className="w-full py-3 px-5 flex flex-col bg-gray-100 rounded-md relative border-2 border-transparent focus-within:border-2 focus-within:border-blue-950"
        onKeyDown={onEscape}
      >
        {open ? (
          <DayPicker
            className={clsx(
              'absolute top-14 max-w-[95vw] left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-0 bg-white z-30 text-xs md:text-base rounded-md shadow-lg p-2 font-sans',
              {
                'border-2 border-transparent': !hasError,
                'border-2 border-red-200': hasError,
              },
            )}
            mode="single"
            fromDate={min}
            toDate={max}
            onSelect={onDateChange}
            disabled={[
              (m) =>
                !dates?.includes(getDateString(m)) ||
                isDisabled(m, tents, availableDates),
            ]}
            showOutsideDays
            fixedWeeks
            selected={value}
            disableNavigation={false}
            modifiers={{
              bathingDates,
              unavailableDates: (m) =>
                (m >= offeringStartsFrom &&
                  m < offeringEndsAt &&
                  !dates?.includes(getDateString(m))) ||
                isUnavailable(m, tents, availableDates),
            }}
            modifiersStyles={{
              bathingDates: { background: 'orange' },
              unavailableDates: { background: 'red' },
            }}
            footer={<DateFieldFooter onClose={onClose} />}
          />
        ) : null}
        <label className="font-sans text-gray-250 font-regular text-sm">{label}*</label>
        <DisplayDate
          isRequired={isRequired}
          isMobile={isMobile}
          onOpen={onOpen}
          onClose={onClose}
          value={value}
          label={label}
        />
      </div>
      {error ? <span className="text-red-200 pl-5">{error}</span> : null}
    </div>
  );
};

type DateFieldFooterProps = {
  onClose: VoidFunction;
};

function DateFieldFooter({ onClose }: DateFieldFooterProps): React.ReactNode {
  return (
    <>
      <button
        type="button"
        className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-blue-100 rounded-full w-6 h-6 md:w-8 md:h-8 flex justify-center items-center"
      >
        <Sprite
          name="close"
          className="w-3 md:w-4"
          width="25"
          height="24"
          strokeWidth={3}
          onClick={onClose}
        />
      </button>
      <div className="flex flex-col">
        <span className="text-xs md:text-base">🟠 - bathing date prices apply</span>
        <span className="text-xs md:text-base">🔴 - fully booked</span>
      </div>
    </>
  );
}

type DisplayDateProps = {
  isRequired: boolean;
  isMobile?: boolean;
  onOpen: VoidFunction;
  onClose: VoidFunction;
  label: string;
  value: Date | undefined;
};

const emptyDate = 'dd/mm/yyyy';

function DisplayDate({
  isRequired,
  onOpen,
  value,
  isMobile = false,
  label,
}: DisplayDateProps) {
  const date = value ? format(value, 'dd/MM/yyyy') : emptyDate;

  const inputRef = useRef<HTMLInputElement>(null);

  const onBlur = useCallback(
    (evt: React.FocusEvent<HTMLInputElement, Element>): void => {
      const parentElement = document.querySelector(
        `[data-container="${label}${isMobile ? '-mobile' : ''}"]`,
      );

      if (parentElement?.contains(evt.relatedTarget as Node)) {
        evt.preventDefault();
        evt.stopPropagation();
        inputRef.current?.focus();
        return;
      }

      // onClose();
    },
    [isMobile, label],
  );

  return (
    <input
      ref={inputRef}
      type="text"
      className="outline-none text-base w-full md:text-base border-none sm:w-1/2 pt-1.5 m-0 bg-gray-100"
      placeholder="dd/mm/2025"
      required={isRequired}
      onClick={onOpen}
      onChange={() => {}}
      onFocus={onOpen}
      onBlur={onBlur}
      value={date}
      id={`start-date-field${isMobile ? '-mobile' : ''}`}
    />
  );
}
